import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Switch,
    FormControlLabel,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { displayValue } from './ledgerDiff';
// Reuse the Statement of Taxes audit styling so every accounting preview shares the same table look.
import '../../statementTaxes/preview/StatementTaxesAuditPreviewDialog.css';
import './LedgerAuditPreviewDialog.css';

const PRIMARY = '#005E73';

// Non-money columns across the ledger sub-tables — rendered as plain text (no leading "$").
const TEXT_COLUMN_IDS = ['name', 'carrierName', 'supplierName', 'description', 'date'];

class LedgerAuditPreviewDialog extends React.Component {
    state = {
        showUnchanged: false,
    };

    toggleUnchanged = () => this.setState(prev => ({ showUnchanged: !prev.showUnchanged }));

    // Zero / empty money values are blank in the ledger tables, so blank them here too — except
    // inside an actual change (e.g. 0.00 -> 100), where the zero is meaningful.
    isZeroOrBlank = (value) => {
        if (value === null || value === undefined || value === '') return true;
        const stripped = String(value).replace(/[(),]/g, '').trim();
        if (stripped === '') return true;
        const n = parseFloat(stripped);
        return !isNaN(n) && n === 0;
    };

    // A money line — dollar pinned left, amount pinned right, exactly like the ledger table cells.
    renderMoneyLine = (value, variant, cellId, mapName, blankZero = true) => {
        const text = (blankZero && this.isZeroOrBlank(value)) ? '' : displayValue(value, cellId, mapName);
        return (
            <div className={`sta-money sta-money-${variant}`}>
                <span className="sta-dollar">{text === '' ? '' : '$'}</span>
                <span className="sta-amount">{text === '' ? ' ' : text}</span>
            </div>
        );
    };

    // A text line (name / date / description) — formatted via displayValue (handles date trimming
    // and marketplace name mapping), no dollar sign.
    renderTextLine = (value, variant, cellId, mapName) => {
        const text = displayValue(value, cellId, mapName);
        return <div className={`sta-text sta-text-${variant}`}>{text === '' ? ' ' : text}</div>;
    };

    renderCell = (section, row, cell) => {
        const field = row.fields[cell.id] || {};
        const isText = TEXT_COLUMN_IDS.includes(cell.id);
        const line = isText
            ? (value, variant, blankZero) => this.renderTextLine(value, variant, cell.id, section.mapName)
            : (value, variant, blankZero) => this.renderMoneyLine(value, variant, cell.id, section.mapName, blankZero);
        const cellClass = `sta-cell ${isText ? 'sta-cell-cat' : 'sta-cell-num'}`;

        if (row.status === 'removed') {
            return <td key={cell.id} className={cellClass}>{line(field.old, 'removed', true)}</td>;
        }
        if (row.status === 'added') {
            return <td key={cell.id} className={cellClass}>{line(field.new, 'added', true)}</td>;
        }
        if (field.changed) {
            return (
                <td key={cell.id} className={cellClass}>
                    {line(field.old, 'old', false)}
                    {line(field.new, 'new', false)}
                </td>
            );
        }
        const val = field.new !== undefined ? field.new : field.old;
        return <td key={cell.id} className={cellClass}>{line(val, 'plain', true)}</td>;
    };

    renderSection = (section) => {
        const { showUnchanged } = this.state;
        // Total rows are part of the table layout — always show them, even when hiding unchanged rows.
        const visibleRows = section.rows.filter(r => showUnchanged || r.status !== 'unchanged' || r.isTotal);
        const { changed, added, removed } = section.counts;
        // A changed Total / grand-total row counts as a change for the header tag too, so a section
        // whose only change is its rolled-up total isn't mislabelled "no changes".
        const totalChanged = section.rows.some(r => r.isTotal && r.status === 'changed');
        const hasChanges = changed + added + removed > 0 || totalChanged;
        const idField = section.idField;
        // When only total rows are visible (e.g. an empty "Other" section showing just its grand
        // total), the column header is noise — show only the section title, divider and the total.
        const hasVisibleDataRows = visibleRows.some(r => !r.isTotal);

        return (
            <div className="lap-section" key={section.key}>
                <div className="sta-section-head">
                    <span className="sta-section-title">{section.title}</span>
                    <span className="sta-section-counts">
                        {changed > 0 && <span className="sta-tag sta-tag-changed">{changed} changed</span>}
                        {added > 0 && <span className="sta-tag sta-tag-added">{added} new</span>}
                        {removed > 0 && <span className="sta-tag sta-tag-removed">{removed} removed</span>}
                        {!hasChanges && <span className="sta-tag sta-tag-none">no changes</span>}
                    </span>
                </div>

                {visibleRows.length === 0 ? (
                    <div className="sta-section-empty">No changes in this section.</div>
                ) : (
                    <div className="lap-table-wrap">
                        <table className="sta-table lap-table">
                            {hasVisibleDataRows && (
                            <thead>
                                <tr>
                                    {section.cellConfig.map((cell) => (
                                        <th
                                            key={cell.id}
                                            className={`sta-th ${TEXT_COLUMN_IDS.includes(cell.id) ? 'sta-th-left' : 'sta-th-right'}`}
                                        >
                                            {cell.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            )}
                            <tbody>
                                {visibleRows.map((row, idx) => (
                                    <tr key={`${section.key}-${row.id}-${idx}`} className={`sta-row sta-row-${row.status}${row.bold ? ' sta-row-bold' : ''}`}>
                                        {section.cellConfig.map((cell) => this.renderCell(section, row, cell))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    render() {
        const { open, loading, ledgerName, diff, onApply, onReject } = this.props;
        const counts = (diff && diff.counts) || { changed: 0, added: 0, removed: 0 };
        const totalChanges = counts.changed + counts.added + counts.removed;
        const allSections = (diff && diff.sections) || [];
        const noChanges = !loading && totalChanges === 0;
        const { showUnchanged } = this.state;

        // Only surface sections that actually changed, unless the user opts to see everything. A
        // section also counts as changed when one of its Total / grand-total rows changed (e.g. the
        // "<ledgerName> Total" moves because other sections changed) even if its own data rows did not.
        const sections = showUnchanged
            ? allSections
            : allSections.filter(s => {
                const dataChanges = s.counts.changed + s.counts.added + s.counts.removed;
                const totalChanged = s.rows.some(r => r.isTotal && r.status === 'changed');
                return dataChanges > 0 || totalChanged;
            });

        return (
            <Dialog
                open={open}
                onClose={onReject}
                maxWidth="lg"
                fullWidth
                classes={{ paper: 'sta-paper' }}
            >
                <div className="sta-header">
                    <div className="sta-header-main">
                        <div className="sta-title-block">
                            <span className="sta-eyebrow">Refresh</span>
                            <span className="sta-title">{ledgerName}</span>
                        </div>
                        <IconButton onClick={onReject} className="sta-close" size="small">
                            <CloseIcon fontSize="small" htmlColor="#ffffff" />
                        </IconButton>
                    </div>
                    <div className="sta-summary">
                        <span className="sta-chip sta-chip-changed">{counts.changed} Changed</span>
                        <span className="sta-chip sta-chip-added">{counts.added} New</span>
                        <span className="sta-chip sta-chip-removed">{counts.removed} Removed</span>
                    </div>
                </div>

                {!loading && !noChanges && (
                    <div className="sta-subbar">
                        <div className="sta-legend">
                            <span className="sta-legend-item"><i className="sta-dot sta-dot-changed" />Changed value</span>
                            <span className="sta-legend-item"><i className="sta-dot sta-dot-added" />New row</span>
                            <span className="sta-legend-item"><i className="sta-dot sta-dot-removed" />Removed row</span>
                        </div>
                        <FormControlLabel
                            className="sta-toggle"
                            control={
                                <Switch
                                    size="small"
                                    checked={this.state.showUnchanged}
                                    onChange={this.toggleUnchanged}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY },
                                    }}
                                />
                            }
                            label="Show unchanged"
                        />
                    </div>
                )}

                <DialogContent className="sta-content" dividers>
                    {loading ? (
                        <div className="sta-loading">
                            <CircularProgress style={{ color: PRIMARY }} />
                            <span className="sta-loading-text">Calculating changes…</span>
                        </div>
                    ) : noChanges ? (
                        <div className="sta-nochanges">
                            <CheckCircleOutlineIcon style={{ color: PRIMARY, fontSize: 48 }} />
                            <span className="sta-nochanges-title">No changes detected</span>
                            <span className="sta-nochanges-sub">Refreshing now would not modify this ledger.</span>
                        </div>
                    ) : (
                        sections.map(this.renderSection)
                    )}
                </DialogContent>

                <DialogActions className="sta-actions">
                    <div className="sta-actions-buttons">
                        <Button onClick={onReject} variant="outlined" className="sta-btn-cancel">
                            Cancel
                        </Button>
                        <Tooltip title={noChanges ? 'Nothing to apply' : ''} disableHoverListener={!noChanges}>
                            <span>
                                <Button
                                    onClick={onApply}
                                    variant="contained"
                                    disableElevation
                                    disabled={loading || noChanges}
                                    className="sta-btn-apply"
                                >
                                    Apply Changes
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </DialogActions>
            </Dialog>
        );
    }
}

export default LedgerAuditPreviewDialog;
